import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { ArrowPathIcon, StarIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { PencilIcon, X, Check } from 'lucide-react'
import { getQueryHistoryForChallenge, toggleStarQuery, updateQueryAlias, QueryHistoryEntry } from './queryHistoryStorage'
import { useTar } from '@/modules'
import { queryHistoryAtom } from './historyAtoms'
import { useAtom } from 'jotai'
import { trackSql } from '@/modules/sqlAnalytics'


interface QueryHistoryProps {
    challengeId: string
    onQuerySelect?: (query: string) => void
}

export const QueryHistory = ({ challengeId, onQuerySelect }: QueryHistoryProps) => {
    const { t } = useTar()
    const [queryHistory, setQueryHistory] = useAtom(queryHistoryAtom)
    const [editingQueryTimestamp, setEditingQueryTimestamp] = useState<number | null>(null)
    const [editingValue, setEditingValue] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null)

    // Load and filter history for current challenge
    const loadHistory = useCallback(() => {
        const history = getQueryHistoryForChallenge(challengeId)
        setQueryHistory(history)
    }, [challengeId])

    useEffect(() => {
        loadHistory()
    }, [loadHistory])

    // Focus and select input when entering edit mode
    useEffect(() => {
        if (editingQueryTimestamp !== null && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [editingQueryTimestamp])

    // Sort history - starred items first (already filtered by challengeId in loadHistory)
    const sortedHistory = useMemo(() => {
        return queryHistory.sort((a, b) => {
            // Starred items first
            if (a.starred && !b.starred) return -1
            if (!a.starred && b.starred) return 1
            // Then sort by timestamp (newest first)
            return b.timestamp - a.timestamp
        })
    }, [queryHistory])

    const historyItemClick = (query: string, isStarred?: boolean) => {
        trackSql('sql:toolbar:history_select', { challengeId, isStarred: !!isStarred })
        onQuerySelect?.(query)
    }

    const handleToggleStar = (timestamp: number) => {
        trackSql('sql:toolbar:history_star', { challengeId })
        toggleStarQuery(challengeId, timestamp)
        loadHistory()
    }

    const handleEditQuery = ({ timestamp, alias, query }: QueryHistoryEntry) => {
        if (editingQueryTimestamp !== timestamp) {
            // Entering edit mode - populate with query
            setEditingValue(alias ?? query)
            setEditingQueryTimestamp(timestamp)
        } else {
            // Exiting edit mode
            setEditingQueryTimestamp(null)
            setEditingValue('')
        }
    }

    const handleSaveEdit = (timestamp: number) => {
        // Update description with the edited value
        updateQueryAlias(challengeId, timestamp, editingValue)
        // Update local state
        setQueryHistory(prev => prev.map(entry =>
            entry.timestamp === timestamp ? { ...entry, alias: editingValue } : entry
        ))
        setEditingQueryTimestamp(null)
        setEditingValue('')
    }

    const handleCancelEdit = (timestamp: number) => {
        setEditingQueryTimestamp(null)
        setEditingValue('')
    }

    if (sortedHistory.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
                <ArrowPathIcon className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">{t('common.noHistory')}</p>
                <p className="text-sm">{t('common.noHistoryDescription')}</p>
            </div>
        )
    }

    return (
        <TooltipProvider>
            <div className="flex flex-col h-full overflow-auto custom-scrollbar ">
                <div className="p-4 space-y-2">
                    {sortedHistory.map(({ query, timestamp, alias, starred }, index) => {
                        return (
                            <div key={`${timestamp}-${index}`} className="rounded-sm cursor-pointer group" >
                                <div className="flex items-center gap-2">
                                    {editingQueryTimestamp === timestamp ? (
                                        <div className="flex-1 flex items-center gap-1">
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                value={editingValue}
                                                onChange={(e) => setEditingValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') {
                                                        handleCancelEdit(timestamp)
                                                    } else if (e.key === 'Enter') {
                                                        handleSaveEdit(timestamp)
                                                    }
                                                }}
                                                placeholder={t('common.enterQuery')}
                                                className="flex-1 text-xs font-mono text-foreground bg-background border border-input rounded-md px-3 py-2 outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring focus:ring-offset-1"
                                            />
                                        </div>
                                    ) : (
                                        <Tooltip>
                                            <TooltipTrigger asChild onClick={() => historyItemClick(query, starred)}>
                                                <pre className="text-xs py-1.5 px-2 font-mono text-foreground whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer flex-1 bg-card p-1 rounded-sm">
                                                    {alias || query}
                                                </pre>
                                            </TooltipTrigger>
                                            <TooltipContent side="right" className="max-w-md overflow-hidden">
                                                <pre className="text-xs text-foreground font-mono whitespace-pre-wrap break-words bg-card p-1 rounded-sm overflow-hidden">
                                                    {query}
                                                </pre>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        {editingQueryTimestamp === timestamp ? (
                                            <>
                                                <button
                                                    onClick={() => handleCancelEdit(timestamp)}
                                                    className="p-1 rounded hover:bg-primary/20 transition-all"
                                                    aria-label="Cancel editing"
                                                    title="Cancel editing"
                                                >
                                                    <X className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                                                </button>
                                                <button
                                                    onClick={() => handleSaveEdit(timestamp)}
                                                    className="p-1 rounded hover:bg-primary/20 transition-all"
                                                    aria-label="Save changes"
                                                    title="Save changes"
                                                >
                                                    <Check className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => handleToggleStar(timestamp)}
                                                    className="group-hover:opacity-100 p-1 rounded hover:bg-primary/20 transition-all"
                                                    aria-label={starred ? t('common.unstarQuery') : t('common.starQuery')}
                                                    title={starred ? t('common.unstarQuery') : t('common.starQuery')}
                                                >
                                                    {starred ? (
                                                        <StarIconSolid className="w-3.5 h-3.5 text-primary" />
                                                    ) : (
                                                        <StarIcon className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleEditQuery({ timestamp, alias, query })}
                                                    className="group-hover:opacity-100 p-1 rounded hover:bg-primary/20 transition-all"
                                                    aria-label={t('common.editAlias')}
                                                    title={t('common.editAlias')}
                                                >
                                                    <PencilIcon className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </TooltipProvider>
    )
}

